/**
 * Recurring Bills — list of suppliers / vendors we pay on a
 * recurring cadence (weekly, fortnightly, monthly, quarterly, annual,
 * biennial). Click a supplier to expand and see every recurring bill
 * for them with amount, cadence, notes, invoice/PO, etc.
 *
 * A bill appears here whenever its `frequency` field is set to
 * anything other than `one_off`. Update a bill's `frequency` via the
 * standard PUT /api/bills/{id} endpoint (or the bill detail page) —
 * it'll appear here immediately after.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Repeat, Search, Loader2, ChevronDown, ChevronRight, DollarSign,
  Building2, Calendar, Tag, FileText, ExternalLink,
} from "lucide-react";

const fmtAUD = (v) => new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD", maximumFractionDigits: 2,
}).format(Number(v) || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU", {
    day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};

const CADENCE_LABEL = {
  weekly: "Weekly", fortnightly: "Fortnightly", monthly: "Monthly",
  quarterly: "Quarterly", annual: "Annual", biennial: "Biennial",
};

const STATUS_COLOR = {
  "New": "bg-sky-100 text-sky-800 border-sky-200",
  "In Progress": "bg-amber-100 text-amber-800 border-amber-200",
  "Paid": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Overdue": "bg-red-100 text-red-800 border-red-200",
  "Awaiting Approval": "bg-violet-100 text-violet-800 border-violet-200",
};

export default function RecurringBillsPage() {
  const [data, setData] = useState({ totals: {}, suppliers: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bills/recurring");
        setData(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load recurring bills");
      } finally { setLoading(false); }
    })();
  }, []);

  const suppliers = useMemo(() => {
    let arr = data.suppliers || [];
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((s) =>
        (s.supplier_name || "").toLowerCase().includes(q) ||
        (s.supplier_email || "").toLowerCase().includes(q) ||
        s.bills.some((b) =>
          (b.invoice_number || "").toLowerCase().includes(q) ||
          (b.reference || "").toLowerCase().includes(q) ||
          (b.notes || "").toLowerCase().includes(q)));
    }
    return arr;
  }, [data.suppliers, search]);

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="recurring-bills-page">
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-gradient-to-br from-fuchsia-500 to-rose-600 text-white">
          <Repeat className="h-6 w-6" />
        </div>
        <div>
          <div className="label-overline">Bills &amp; Payables</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">
            Recurring Bills
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
            Every vendor / supplier we pay on a recurring schedule. Click
            a supplier to expand and see every recurring bill with
            amounts, subscription type, cadence and payment status.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Building2} label="Suppliers" value={data.totals?.supplier_count ?? 0} />
        <StatCard icon={FileText} label="Recurring bills" value={data.totals?.bill_count ?? 0} />
        <StatCard icon={DollarSign} label="Monthly spend" value={fmtAUD(data.totals?.monthly_total)} accent="rose" />
        <StatCard icon={DollarSign} label="Annualised" value={fmtAUD(data.totals?.annual_total)} accent="rose" />
      </div>

      <div className="max-w-md">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by supplier, invoice number or reference…"
            className="rounded-sm pl-8 h-9"
            data-testid="recurring-bills-search" />
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <Loader2 className="h-6 w-6 animate-spin inline text-zinc-400" />
        </div>
      ) : suppliers.length === 0 ? (
        <Card className="rounded-sm"><CardContent className="p-8 text-center text-sm text-zinc-500 space-y-2">
          <div>No recurring bills yet.</div>
          <div className="text-[11px] text-zinc-400">
            Tag a bill with a recurring frequency (weekly / monthly / annual etc)
            in the bill detail view to have it appear here.
          </div>
        </CardContent></Card>
      ) : (
        <Card className="rounded-sm border-zinc-200">
          <CardContent className="p-0">
            {suppliers.map((s) => {
              const key = s.supplier_name;
              const isOpen = !!expanded[key];
              return (
                <div key={key}
                  className="border-b border-zinc-100 last:border-b-0"
                  data-testid={`recurring-bills-supplier-${key.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
                  <button type="button"
                    onClick={() => setExpanded((e) => ({ ...e, [key]: !isOpen }))}
                    className="w-full text-left flex items-center gap-3 p-4 hover:bg-zinc-50">
                    {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-500 shrink-0" />
                           : <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate">{s.supplier_name}</div>
                      {(s.supplier_email || s.supplier_abn) && (
                        <div className="text-[11px] text-zinc-500 truncate">
                          {s.supplier_email}
                          {s.supplier_email && s.supplier_abn && " · "}
                          {s.supplier_abn && `ABN ${s.supplier_abn}`}
                        </div>
                      )}
                      <div className="text-[10px] text-zinc-500 mt-0.5">
                        {s.bill_count} recurring bill{s.bill_count === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="text-right text-xs shrink-0">
                      <div className="font-semibold text-rose-700">
                        {fmtAUD(s.monthly_equivalent_amount)}<span className="text-[10px] text-zinc-400"> /mo</span>
                      </div>
                      <div className="text-[10px] text-zinc-500">
                        {fmtAUD(s.annual_equivalent_amount)} /yr
                      </div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="bg-zinc-50 border-t border-zinc-100 divide-y divide-zinc-100">
                      {s.bills.map((b) => (
                        <div key={b.bill_id}
                          className="p-3 grid grid-cols-1 md:grid-cols-6 gap-3 items-start"
                          data-testid={`recurring-bill-${b.bill_id}`}>
                          <div className="md:col-span-2">
                            <div className="text-sm font-medium">
                              {b.invoice_number || "(no invoice #)"}
                            </div>
                            {b.reference && (
                              <div className="text-[11px] text-zinc-500 truncate">
                                Ref: {b.reference}
                              </div>
                            )}
                            {b.notes && (
                              <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">
                                {b.notes}
                              </div>
                            )}
                            <div className="flex items-center gap-1 flex-wrap mt-1.5">
                              {b.bill_type && (
                                <Badge variant="outline"
                                  className="text-[9px] rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200">
                                  <Tag className="h-2 w-2 mr-0.5" />{b.bill_type}
                                </Badge>
                              )}
                              {b.status && (
                                <Badge variant="outline"
                                  className={`text-[9px] rounded-sm ${STATUS_COLOR[b.status] || "bg-zinc-100 text-zinc-700 border-zinc-200"}`}>
                                  {b.status}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div>
                            <Badge variant="outline"
                              className="rounded-sm bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200 text-[10px]">
                              {CADENCE_LABEL[b.frequency] || b.frequency}
                            </Badge>
                            {b.amount_type && (
                              <div className="text-[10px] text-zinc-500 mt-1">
                                {b.amount_type}
                              </div>
                            )}
                            {b.payment_method && (
                              <div className="text-[10px] text-zinc-500 mt-0.5">
                                via {b.payment_method}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-zinc-500">
                            <div className="label-overline">Due</div>
                            <div className="text-[11px] flex items-center gap-1">
                              <Calendar className="h-2.5 w-2.5" />
                              {fmtDate(b.due_date)}
                            </div>
                            {b.issue_date && (
                              <div className="text-[10px] text-zinc-400 mt-0.5">
                                Issued: {fmtDate(b.issue_date)}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-zinc-500">
                            {b.entity_to_be_invoiced && (
                              <>
                                <div className="label-overline">Entity</div>
                                <div className="text-[11px] truncate">
                                  {b.entity_to_be_invoiced}
                                </div>
                              </>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-sm text-rose-700">
                              {fmtAUD(b.total)}
                            </div>
                            {b.gst_amount != null && (
                              <div className="text-[10px] text-zinc-400">
                                incl. {fmtAUD(b.gst_amount)} GST
                              </div>
                            )}
                            <Link to={`/bills/${b.bill_id}`}
                              className="text-[10px] text-[#002FA7] hover:underline mt-1 inline-flex items-center gap-0.5">
                              Open <ExternalLink className="h-2.5 w-2.5" />
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card className="rounded-sm border-zinc-200">
      <CardContent className="p-3">
        <div className="label-overline flex items-center gap-1.5">
          <Icon className="h-3 w-3" />{label}
        </div>
        <div className={`text-2xl font-bold mt-1 ${accent === "rose" ? "text-rose-700" : ""}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
